import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react'
import { Loader2 } from 'lucide-react'
import { cn } from '../lib/cn.js'

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'accent' | 'link'
export type ButtonSize = 'sm' | 'md' | 'lg' | 'icon'

const variants: Record<ButtonVariant, string> = {
  primary: 'bg-primary text-fg-on-primary hover:bg-primary-hover active:bg-primary-active shadow-sm',
  secondary: 'bg-surface text-fg border border-border hover:bg-surface-hover active:bg-surface-active shadow-sm',
  ghost: 'text-fg-muted hover:bg-surface-hover hover:text-fg active:bg-surface-active',
  danger: 'bg-danger text-white hover:brightness-95 active:brightness-90 shadow-sm',
  accent: 'bg-accent text-[var(--najah-neutral-900)] hover:brightness-95 active:brightness-90 shadow-sm',
  link: 'text-primary hover:underline underline-offset-4 p-0 h-auto',
}

const sizes: Record<ButtonSize, string> = {
  sm: 'h-8 px-3 text-sm gap-1.5 rounded-md',
  md: 'h-10 px-4 text-sm gap-2 rounded-lg',
  lg: 'h-12 px-6 text-base gap-2.5 rounded-lg',
  icon: 'size-10 rounded-lg',
}

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant
  size?: ButtonSize
  loading?: boolean
  /** يظهر في بداية الزر (يمين في RTL) */
  startIcon?: ReactNode
  endIcon?: ReactNode
  fullWidth?: boolean
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { className, variant = 'primary', size = 'md', loading, startIcon, endIcon, fullWidth, disabled, children, ...props },
  ref,
) {
  return (
    <button
      ref={ref}
      // زر قيد التحميل يبقى معطّلاً لمنع الإرسال المزدوج
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={cn(
        'inline-flex items-center justify-center font-medium select-none',
        'transition-[background-color,color,box-shadow,transform] duration-150 ease-[var(--ease-out-quint)]',
        'focus-visible:outline-2 focus-visible:outline-ring focus-visible:outline-offset-2',
        'disabled:opacity-50 disabled:pointer-events-none active:scale-[0.985]',
        variants[variant],
        variant !== 'link' && sizes[size],
        fullWidth && 'w-full',
        className,
      )}
      {...props}
    >
      {loading ? <Loader2 className="size-4 animate-spin" aria-hidden /> : startIcon}
      {children}
      {!loading && endIcon}
    </button>
  )
})
